import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MailIcon, MessageCircleIcon, PhoneIcon, PlusIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { CONTACT } from "../data/mccContent";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
const QUESTIONS = [{
  q: "How much are tuition and boarding fees?",
  a: "Fee schedules are issued directly by the MCC office for each academic year. Message, call or email the team to receive the current schedule."
}, {
  q: "Can fees be paid in instalments?",
  a: "MCC’s published FAQ indicates that instalment payment is possible, subject to terms and conditions. Confirm the current arrangement with Admissions."
}, {
  q: "Is the application fee refundable?",
  a: "MCC’s admissions guidance states that the application fee is non-refundable and payable before submission. It can also be paid in Naira."
}, {
  q: "Are deposits refundable?",
  a: "MCC’s published FAQ notes that certain deposits, such as security and boarding deposits, may be refunded at the end of the year when furniture and appliances are left in good condition. Confirm current policy."
}];
export function Fees() {
  const [open, setOpen] = useState<number | null>(0);
  const reduce = useReducedMotion();
  const whatsapp = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent("Hello MCC, I would like the current fee schedule and payment information.")}`;
  const methods = [{
    icon: MessageCircleIcon,
    label: "Ask about fees",
    detail: "WhatsApp the Admissions team",
    href: whatsapp,
    external: true
  }, {
    icon: PhoneIcon,
    label: "Call MCC",
    detail: "09062540705 · 07076529048",
    href: "tel:+2349062540705",
    external: false
  }, {
    icon: MailIcon,
    label: "Email MCC",
    detail: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
    external: false
  }];
  return <>
      <PageHero variant="type" eyebrow="FEES & PAYMENTS" title="Clear answers, straight from MCC." description="Fees change by academic year. We’ll always give you the current figures directly." breadcrumb="Fees & Payments" data-id="element-1015" data-mp="977mtoa7cd16" />

      <main data-id="element-1016" data-mp="n55vqe5r7h9a">
        <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-9 md:py-24" data-id="element-1017" data-mp="n3l16ojfvybf">
          <div className="grid gap-px bg-[#dedbd3] md:grid-cols-3" data-id="element-1018" data-mp="3lmry0hx6kos1">
            {methods.map((m, i) => <Reveal key={m.label} delay={i * 0.08} data-id="element-1019" data-mp="3qkl5dea7zotw" data-mpi={i}>
                <a href={m.href} target={m.external ? "_blank" : undefined} rel={m.external ? "noreferrer" : undefined} className="group flex h-full min-h-[220px] flex-col justify-between bg-[#fbfaf6] p-7 transition-colors duration-200 hover:bg-[#061f38] hover:text-white" data-id="element-1020" data-mp="3qj08vj4chvte">
                  <m.icon size={22} className="text-[#bb1e35] transition-colors duration-200 group-hover:text-[#f0b1b9]" data-id="element-1021" data-mp="32v8v0b35paq0" />
                  <div data-id="element-1022" data-mp="37siivmawdebq">
                    <h2 className="text-2xl font-medium tracking-[-0.04em]" data-id="element-1023" data-mp="30b2gxaktw3vq">{m.label}</h2>
                    <p className="mt-2 text-sm text-[#4f5963] transition-colors duration-200 group-hover:text-white/70" data-id="element-1024" data-mp="2vc9d69vcfr8r">{m.detail}</p>
                  </div>
                </a>
              </Reveal>)}
          </div>
        </section>

        <section className="border-t border-[#dedbd3] bg-[#f2f0eb] py-24 md:py-32" data-id="element-1025" data-mp="2lda2klr0plj4">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 md:px-9 lg:grid-cols-12" data-id="element-1026" data-mp="2hcqavxp7ra4d">
            <div className="lg:col-span-4" data-id="element-1027" data-mp="2clhwen910ykc">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1028" data-mp="2ckgi89wprvye">QUESTIONS PARENTS ASK</p>
              <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-5xl" data-id="element-1029" data-mp="27kkdg8g700hs">Before you pay anything.</h2>
            </div>
            <div className="border-t border-[#061f38] lg:col-span-7 lg:col-start-6" data-id="element-1030" data-mp="1xoqykif03uwu">
              {QUESTIONS.map((item, i) => {
              const isOpen = open === i;
              return <div key={item.q} className="border-b border-[#cfcac0]" data-id="element-1031" data-mp="2iel1ixfgmmop" data-mpi={i}>
                    <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-6 py-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bb1e35]" data-id="element-1032" data-mp="2ncd18dmcjd3g">
                      <span className="text-xl font-medium tracking-[-0.03em] md:text-2xl" data-id="element-1033" data-mp="2dl0cvvzrb526">{item.q}</span>
                      <PlusIcon size={20} className={`shrink-0 text-[#bb1e35] transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} data-id="element-1034" data-mp="1zlqyn1v42l0u" />
                    </button>
                    <AnimatePresence initial={false} data-id="element-1035" data-mp="1qnoz6qr100fo">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: "auto",
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: reduce ? 0.01 : 0.35,
                    ease: EASE
                  }} className="overflow-hidden" data-id="element-1036" data-mp="1up0g87vu3dc9">
                          <p className="max-w-xl pb-7 text-base leading-7 text-[#4f5963]" data-id="element-1037" data-mp="3ozw046cgyeuc">{item.a}</p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-9 md:py-36" data-id="element-1038" data-mp="1n6j0vl1a14bd">
          <WordReveal as="p" text="No outdated figures, no surprises. Every family receives the current fee schedule directly from the MCC office." className="max-w-4xl text-3xl font-medium leading-[1.12] tracking-[-0.045em] md:text-5xl" data-id="element-1039" data-mp="1icevh67nt4ce" />
        </section>
      </main>

      <CTABand title="Plan with confidence. Ask for the current schedule." image={IMAGES.campus} data-id="element-1040" data-mp="26296rjcbykuj" />
    </>;
}